import { useCallback, useMemo } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";

type DishDetailsModalLocationState = {
  closeTo?: string;
};

const parsePositiveNumber = (value: string | null): number | null => {
  if (!value) return null;
  const parsedNumber = Number(value);
  if (!Number.isFinite(parsedNumber)) return null;
  if (parsedNumber <= 0) return null;
  return parsedNumber;
};

export const useDishDetailsModalRouting = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const dishId = useMemo(() => {
    return parsePositiveNumber(searchParams.get("dishId"));
  }, [searchParams]);

  const categoryId = useMemo(() => {
    return parsePositiveNumber(searchParams.get("categoryId"));
  }, [searchParams]);

  const openDishDetailsModal = useCallback(
    (nextDishId: number, nextCategoryId?: number) => {
      const nextSearchParams = new URLSearchParams(searchParams);

      nextSearchParams.set("dishId", String(nextDishId));

      if (nextCategoryId != null) {
        nextSearchParams.set("categoryId", String(nextCategoryId));
      } else {
        nextSearchParams.delete("categoryId");
      }

      navigate(
        {
          pathname: location.pathname,
          search: nextSearchParams.toString(),
        },
        {
          state: {
            closeTo: `${location.pathname}${location.search}`,
          } satisfies DishDetailsModalLocationState,
        }
      );
    },
    [location.pathname, location.search, navigate, searchParams]
  );

  const closeDishDetailsModal = useCallback(() => {
    const locationState =
      location.state as DishDetailsModalLocationState | null;

    if (locationState?.closeTo) {
      navigate(locationState.closeTo, { replace: true });
      return;
    }

    const nextSearchParams = new URLSearchParams(searchParams);
    nextSearchParams.delete("dishId");
    nextSearchParams.delete("categoryId");

    navigate(
      {
        pathname: location.pathname,
        search: nextSearchParams.toString(),
      },
      { replace: true }
    );
  }, [location.pathname, location.state, navigate, searchParams]);

  return {
    dishId,
    categoryId,
    openDishDetailsModal,
    closeDishDetailsModal,
  };
};
